import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { iconSize } from '../../../utils/libs';
import { formatMoneyText } from '../productView/productViewFormat';
import type { InventoryStyles } from './inventoryStyles';
import type { StockValueState } from './useStockValue';
import type { AppliedFilterChip } from '../../../utils/inventoryFilters';

type Props = {
  styles: InventoryStyles;
  theme: any;
  currencyText: string;
  stockValue: StockValueState;
  /** "Total stock value", or "Filtered stock value" when filters apply. */
  stockValueTitle: string;
  onOpenStockValue: () => void;
  countText: string;
  sortLabel: string;
  onOpenSort: () => void;
  filterCount: number;
  onOpenFilters: () => void;
  chips: AppliedFilterChip[];
  onRemoveChip: (chip: AppliedFilterChip) => void;
  onClearFilters: () => void;
  /** Wide web windows: the two values sit side by side instead of splitting the width. */
  wide?: boolean;
};

/**
 * The white section under the header: the Stock value strip (the whole
 * store, or the filtered products; its title opens and closes the figures,
 * closed at first), then "N products · Sort · Filters", then one chip per
 * applied filter.
 */
const InventoryHeaderSection: React.FC<Props> = ({
  styles,
  theme,
  currencyText,
  stockValue,
  stockValueTitle,
  onOpenStockValue,
  countText,
  sortLabel,
  onOpenSort,
  filterCount,
  onOpenFilters,
  chips,
  onRemoveChip,
  onClearFilters,
  wide = false,
}) => {
  // The figures start hidden: the title row opens and closes them.
  const [expanded, setExpanded] = useState(false);
  const summary = stockValue.summary;
  const buying = summary
    ? formatMoneyText(currencyText, summary.buyingValue)
    : '—';
  const selling = summary
    ? formatMoneyText(currencyText, summary.sellingValue)
    : '—';
  const tiles = (
    <View style={styles.stripTiles}>
      <View style={[styles.stripTile, wide && styles.stripTileWide]}>
        <Text style={styles.stripKey}>Selling</Text>
        <Text
          style={styles.stripValue}
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.8}>
          {selling}
        </Text>
      </View>
      <View
        style={[
          styles.stripTile,
          wide && styles.stripTileWide,
          styles.stripTileDivider,
        ]}>
        <Text style={styles.stripKey}>Buying</Text>
        <Text
          style={styles.stripValue}
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.8}>
          {buying}
        </Text>
      </View>
    </View>
  );
  const toolIconColor = theme.colors.textGray700;

  return (
    <View style={styles.headerSection}>
      <View style={[styles.strip, !expanded && styles.stripCollapsed]}>
        <View style={styles.stripHead}>
          <Pressable
            onPress={() => setExpanded(prev => !prev)}
            accessibilityRole="button"
            accessibilityLabel={
              expanded ? `Hide ${stockValueTitle}` : `Show ${stockValueTitle}`
            }
            accessibilityState={{ expanded }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ pressed }) => [
              styles.stripToggle,
              pressed && styles.buttonPressed,
            ]}>
            <Text style={styles.stripTitle}>{stockValueTitle}</Text>
            <MaterialCommunityIcons
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={iconSize(18)}
              color={theme.colors.text}
            />
          </Pressable>
          <Pressable
            onPress={onOpenStockValue}
            accessibilityRole="button"
            accessibilityLabel={`${stockValueTitle}, view breakdown`}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ pressed }) => [
              styles.stripLink,
              pressed && styles.buttonPressed,
            ]}>
            <Text style={styles.stripLinkText}>View breakdown</Text>
            <MaterialCommunityIcons
              name="chevron-right"
              size={iconSize(16)}
              color={theme.colors.primary}
            />
          </Pressable>
        </View>
        {expanded ? (
          <Pressable
            onPress={onOpenStockValue}
            accessibilityRole="button"
            style={({ pressed }) => pressed && styles.buttonPressed}>
            {tiles}
          </Pressable>
        ) : null}
      </View>

      <View style={styles.toolbar}>
        <Text style={styles.count} numberOfLines={1}>
          {countText}
        </Text>
        <Pressable
          onPress={onOpenSort}
          accessibilityRole="button"
          accessibilityLabel={`Sort: ${sortLabel}`}
          style={({ pressed }) => [
            styles.toolButton,
            pressed && styles.toolButtonActive,
          ]}>
          <MaterialCommunityIcons
            name="sort"
            size={iconSize(18)}
            color={toolIconColor}
          />
          <Text style={styles.toolButtonText}>{sortLabel}</Text>
          <MaterialCommunityIcons
            name="chevron-down"
            size={iconSize(16)}
            color={toolIconColor}
          />
        </Pressable>
        <Pressable
          onPress={onOpenFilters}
          accessibilityRole="button"
          accessibilityLabel={
            filterCount ? `Filters, ${filterCount} applied` : 'Filters'
          }
          style={({ pressed }) => [
            styles.toolButton,
            pressed && styles.toolButtonActive,
          ]}>
          <MaterialCommunityIcons
            name="tune-variant"
            size={iconSize(18)}
            color={toolIconColor}
          />
          <Text style={styles.toolButtonText}>Filters</Text>
          {filterCount ? (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{filterCount}</Text>
            </View>
          ) : null}
        </Pressable>
      </View>

      {chips.length ? (
        <View style={styles.applied} accessibilityLabel="Applied filters">
          {chips.map(chip => {
            const [key, ...rest] = chip.label.split(': ');
            const hasKey = rest.length > 0;
            return (
              <View key={chip.id} style={styles.token}>
                <Text style={styles.tokenText}>
                  {hasKey ? (
                    <Text style={styles.tokenKey}>{`${key}: `}</Text>
                  ) : null}
                  {hasKey ? rest.join(': ') : chip.label}
                </Text>
                <Pressable
                  onPress={() => onRemoveChip(chip)}
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${chip.label}`}
                  style={({ pressed }) => [
                    styles.tokenRemove,
                    pressed && styles.buttonPressed,
                  ]}>
                  <MaterialCommunityIcons
                    name="close"
                    size={iconSize(16)}
                    color={theme.colors.textGray600}
                  />
                </Pressable>
              </View>
            );
          })}
          <Pressable
            onPress={onClearFilters}
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.clearLink,
              pressed && styles.buttonPressed,
            ]}>
            <Text style={styles.clearLinkText}>Clear filters</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
};

export default InventoryHeaderSection;
